import React from 'react';
import Link from 'next/link';
import SEOTags from '@/components/SEOTags';
import Footer from '@/pagecomponents/newcomponents/Footer';
import CallToAction from '@/pagecomponents/newcomponents/CallToAction';
import Navbar from "@/pagecomponents/newcomponents/Navbar";
import industriesData from '../../src/seo/industries.json';

const topIndustries = industriesData.map((industry: any) => ({
  name: industry.name,
  slug: industry.slug,
  jobCount: industry.jobCount,
  description: industry.description ?? `Explore job opportunities in ${industry.name}. Discover the best roles, average salaries, and career growth potential in this exciting industry.`,
  avgSalary: industry.avgSalary,
  topRoles: industry.topRoles ?? [],
  growthRate: industry.growthRate,
  featured: industry.featured || false
}));

const IndustryCard = ({ industry, isFeatured = false }) => {
  return (
    <Link href={`/industries/${industry.slug}`}>
      <div className={`group relative bg-white rounded-xl border border-gray-100 hover:border-purple-200 hover:shadow-xl transition-all duration-300 p-6 hover:-translate-y-1 cursor-pointer ${isFeatured ? 'ring-2 ring-purple-100' : ''}`}>
        {/* Featured badge */}
        {isFeatured && (
          <div className="absolute -top-2 -right-2 z-10">
            <span className="bg-gradient-to-r from-purple-500 to-blue-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
              Featured
            </span>
          </div>
        )}

        {/* Gradient accent */}
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-purple-500 to-blue-500 rounded-t-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        
        {/* Header */}
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-xl font-bold text-gray-900 group-hover:text-purple-700 transition-colors">
              {industry.name}
            </h3>
            <p className="text-gray-600 text-sm flex items-center gap-1">
              <span>{industry.jobCount.toLocaleString()} jobs</span>
              <span className="text-green-600 font-medium">{industry.growthRate}</span>
            </p>
          </div>
        </div>

        {/* Description */}
        <p className="text-gray-600 text-sm mb-4 line-clamp-2">
          {industry.description}
        </p>

        {/* Stats */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs text-gray-500 mb-1">Avg Salary Range</div>
            <div className="text-sm font-semibold text-green-600">{industry.avgSalary}</div>
          </div>
        </div>

        {/* Top Roles */}
        <div className="mb-4">
          <div className="text-xs text-gray-500 mb-2">Popular Roles</div>
          <div className="flex flex-wrap gap-1">
            {industry.topRoles.slice(0, 3).map((role, index) => (
              <span 
                key={index}
                className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded-md"
              >
                {role}
              </span>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-50">
          <span className="text-purple-600 font-medium text-sm group-hover:text-purple-700 transition-colors">
            {`View ${industry.name} Jobs →`}
          </span>
          <div className="flex items-center gap-1 text-gray-400">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M6 6V5a5 5 0 0110 0v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-10-1.43V8a2 2 0 012-2h2zm2-1a3 3 0 016 0v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z" clipRule="evenodd" />
            </svg>
          </div>
        </div>
      </div>
    </Link>
  );
};

const TopIndustriesPage = () => {
  const featuredIndustries = topIndustries.filter(industry => industry.featured);
  const otherIndustries = topIndustries.filter(industry => !industry.featured);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50">
      <SEOTags
        title="Top Jobs by Industry | LifeShack"
        description="Explore job opportunities, salary ranges, and in-demand roles across major industries in the United States."
        url="https://www.lifeshack.com/industries/"
      />
      
      <Navbar secondary /> 
      {/* Hero Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Top Jobs by <span className="text-purple-600">Industry</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              {`Discover the fastest-growing industries and highest-paying sectors. Explore career opportunities, salary insights, and in-demand roles across America's top job markets.`}
            </p>
            
            {/* Quick Stats */}
            <div className="flex justify-center items-center gap-8 mb-8">
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-600">
                  1.8M
                </div>
                <div className="text-gray-600 text-sm">Total Jobs</div>
              </div>
              <div className="w-px h-12 bg-gray-300"></div>
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-600">{topIndustries.length}</div>
                <div className="text-gray-600 text-sm">Industries</div>
              </div>
              <div className="w-px h-12 bg-gray-300"></div>
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-600">500+</div>
                <div className="text-gray-600 text-sm">Job Types</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Industries */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Fastest Growing Industries</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Our top industry sectors with the highest growth rates, most job opportunities, and competitive salaries.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {featuredIndustries.map(industry => (
              <IndustryCard key={industry.slug} industry={industry} isFeatured={true} />
            ))}
          </div>
        </div>
      </section>

      {/* All Other Industries */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">More Great Industries</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Explore additional industry sectors with excellent career opportunities and growth potential.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {otherIndustries.map(industry => (
              <IndustryCard key={industry.slug} industry={industry} />
            ))}
          </div>
        </div>
      </section>

      {/* Entry Level Section */}
      <section className="py-16 bg-gradient-to-r from-purple-600 to-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">
            <h2 className="text-3xl font-bold mb-4">{`Just Starting Your Career?`}</h2>
            <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
              Explore entry-level opportunities perfect for new graduates and career changers, or browse internships to gain valuable experience.
            </p>
            <div className="flex justify-center gap-4">
              <Link href="/jobs/">
                <button className="bg-white text-purple-600 font-bold py-3 px-8 rounded-lg hover:bg-gray-100 transition-colors">
                  Browse All Jobs
                </button>
              </Link>
              {/* <Link href="/jobs/internships">
                <button className="border-2 border-white text-white font-bold py-3 px-8 rounded-lg hover:bg-white hover:text-purple-600 transition-colors">
                  Find Internships
                </button>
              </Link> */}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CallToAction />

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default TopIndustriesPage;
